/**
 * Phase 9.3 — Cultural Risk Layer UX primitives.
 *
 * CD R10 Q1 verdict (HIGHEST priority): two orthogonal axes for cultural
 * factors, NEVER conflated.
 *   - EVIDENCE STRENGTH → neutral confidence ramp (how-sure dot scale)
 *   - SENSITIVITY → purple "discretion rail" (handle-with-care border +
 *     "Handle with care" panel listing the specific flags)
 *
 * Reusing synthetic-purple for sensitivity is principled: synthetic-purple
 * already means "shaped by curation/judgment, not raw measurement" —
 * exactly what a curated cultural-risk claim is. No new colour tokens.
 *
 * Anti-stereotyping is the moat — every claim cites a peer-reviewed
 * source; contested claims need ≥2 independent citations; politically-
 * contested entries require human reviewedBy (the audit gate enforces).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { loadPendingCulturalRiskReview } from '@biomax/plugin-region-database';
import type {
  CulturalRiskFactor, EvidenceStrength, CulturalRiskDomain, SensitivityFlag,
  CulturalRiskExampleCase,
} from '@biomax/plugin-region-database';

// ---------------------------------------------------------------------------
// Evidence-strength display: confidence ramp (3-dot indicator + word + colour)
// ---------------------------------------------------------------------------

interface EvidenceMeta {
  word: string;
  cssVar: string;
  dots: number;
  needs2Cites?: boolean;
}

export const EVIDENCE_META: Record<EvidenceStrength, EvidenceMeta> = {
  strong:      { word: 'Strong',      cssVar: 'var(--bm-prov-confirmed, var(--bm-state-live))', dots: 3 },
  moderate:    { word: 'Moderate',    cssVar: 'var(--bm-accent, #6ea3ff)',                       dots: 2 },
  contested:   { word: 'Contested',   cssVar: 'var(--bm-threat-2, #fb923c)',                     dots: 1, needs2Cites: true },
  speculative: { word: 'Speculative', cssVar: 'var(--bm-text-subtle)',                            dots: 1 },
};

export function EvidenceDots({ strength }: { strength: EvidenceStrength }) {
  const m = EVIDENCE_META[strength];
  return (
    <span style={{ display: 'inline-flex', gap: 2 }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          width: 5, height: 5, borderRadius: '50%',
          background: i < m.dots ? m.cssVar : 'var(--bm-border)',
        }} />
      ))}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Domain icons (CD R10 Q4: reuse existing semantic tokens — no new colour)
// ---------------------------------------------------------------------------

export const DOMAIN_ICON: Record<CulturalRiskDomain, string> = {
  'burial-practice':     '⚱',
  'vaccine-hesitancy':   '✣',
  'mass-gathering':      '❖',
  'gender-access':       '⚦',
  'animal-interface':    '⬢',
  'conflict-sanctions':  '⚔',
  'govt-denialism':      '⊘',
  'stigma-underreport':  '◍',
  'wash-blocker':        '≈',
  'trust-index':         '◈',
};

// ---------------------------------------------------------------------------
// Sensitivity flag display
// ---------------------------------------------------------------------------

export const SENSITIVITY_LABEL: Record<SensitivityFlag, string> = {
  'stereotyping-risk':         'Stereotyping risk',
  'politically-contested':     'Politically contested',
  'religious-sensitivity':     'Religious sensitivity',
  'stigma-amplification-risk': 'Stigma-amplification risk',
};

// ---------------------------------------------------------------------------
// Citation superscript — CD R10 Q2 verdict: "both". Always-visible
// superscript [n] in the phenomenon prose (so "a source EXISTS" is
// scannable at a glance) + click/hover popover carrying publisher +
// verbatim quote + the identifier. The existence of a citation is never
// one hover away from invisible — that's the honesty rule.
//
// Popover mechanics mirror HelpButton.tsx (click-outside + Esc close).
// ---------------------------------------------------------------------------

/** Render the identifier line for a citation (PMID / DOI / URL — in that
 *  priority). PMIDs + DOIs round-trip via @biomax/plugin-literature-fetch. */
function citationIdent(c: CulturalRiskExampleCase['citation']): string {
  if (c.pmid) return `PMID ${c.pmid}`;
  if (c.doi) return `DOI ${c.doi}`;
  if (c.url) return c.url;
  return 'identifier pending';
}

export function CitationSup({ n, example }: { n: number; example: CulturalRiskExampleCase }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const c = example.citation;

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span ref={ref} style={{ position: 'relative' as const, display: 'inline' }}>
      <sup
        role="button"
        tabIndex={0}
        aria-label={`Source ${n}: ${c.publisher}${c.year ? ` ${c.year}` : ''}`}
        aria-expanded={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
        style={{
          cursor: 'pointer',
          color: 'var(--bm-accent, #6ea3ff)',
          fontFamily: 'var(--bm-font-mono)', fontSize: '0.72em', fontWeight: 700,
          padding: '0 1px',
        }}
      >[{n}]</sup>
      {open && (
        <span
          role="dialog"
          style={{
            position: 'absolute' as const, bottom: 'calc(100% + 4px)', left: 0, zIndex: 200,
            width: 'min(320px, 78vw)',
            padding: 10,
            background: 'var(--bm-bg-panel, var(--bm-bg))',
            border: '1px solid var(--bm-border)',
            borderRadius: 'var(--bm-radius-sm)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
            fontFamily: 'var(--bm-font-sans)', fontSize: 11.5, lineHeight: 1.5,
            color: 'var(--bm-text)', textAlign: 'left' as const,
            whiteSpace: 'normal' as const, fontWeight: 400,
          }}
        >
          <span style={{
            display: 'block',
            fontFamily: 'var(--bm-font-mono)', fontSize: 9.5, fontWeight: 700,
            letterSpacing: '.4px', textTransform: 'uppercase' as const,
            color: 'var(--bm-accent, #6ea3ff)', marginBottom: 4,
          }}>Source [{n}] · {c.publisher}{c.year ? ` · ${c.year}` : ''}</span>
          <span style={{ display: 'block', fontStyle: 'italic' as const, color: 'var(--bm-text-muted)', marginBottom: 5 }}>
            {c.title}
          </span>
          {/* Verbatim sourced claim — the impact line IS the quote, preserved
              verbatim per the verifier-first rail (no editing for clarity). */}
          <span style={{
            display: 'block', padding: '5px 8px', marginBottom: 5,
            background: 'var(--bm-bg-subtle)',
            borderLeft: '2px solid var(--bm-border-strong, var(--bm-border))',
            borderRadius: 3,
            color: 'var(--bm-text)',
          }}>
            “{example.impact}”
          </span>
          <span style={{
            display: 'block',
            fontFamily: 'var(--bm-font-mono)', fontSize: 10,
            color: 'var(--bm-text-subtle)',
          }}>
            {example.location} · {example.year} · {citationIdent(c)}
          </span>
        </span>
      )}
    </span>
  );
}

/** Render phenomenon prose followed by one always-visible citation
 *  superscript per example case (CD R10 Q2). */
function PhenomenonWithCitations({ factor }: { factor: CulturalRiskFactor }) {
  return (
    <span>
      {factor.phenomenon}
      {factor.exampleCases.map((ex, i) => (
        <CitationSup key={i} n={i + 1} example={ex} />
      ))}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Factor card — the operator-facing rendering of a CulturalRiskFactor.
// Per CD R10:
//   - Sensitive factors get the purple discretion rail (3px left border +
//     "Handle with care" panel)
//   - Evidence strength shows as the dot-ramp + word, NEVER conflated
//     with sensitivity
//   - Citation count chip ("▦ N sources") indicates citation-traceability
//   - Suppress button lets operator hide a factor from the current view
//     (operator decides; system surfaces it but doesn't impose)
// ---------------------------------------------------------------------------

export function CulturalFactorCard({
  factor, onSuppress, suppressed = false,
}: {
  factor: CulturalRiskFactor;
  onSuppress?: () => void;
  suppressed?: boolean;
}) {
  const ev = EVIDENCE_META[factor.evidenceStrength];
  const sensitive = factor.sensitivityFlags.length > 0;
  return (
    <div style={{
      background: 'var(--bm-bg-panel, var(--bm-bg))',
      border: '1px solid var(--bm-border)',
      borderLeft: sensitive ? '3px solid var(--bm-prov-synthetic, var(--bm-blocked))' : undefined,
      borderRadius: 'var(--bm-radius-md)',
      padding: 14,
      opacity: suppressed ? 0.45 : 1,
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: 10, marginBottom: 8,
      }}>
        <span style={{
          fontSize: 12, fontWeight: 700, textTransform: 'capitalize' as const,
          display: 'inline-flex', alignItems: 'center', gap: 7,
        }}>
          <span style={{ fontSize: 15, color: 'var(--bm-text-muted)' }}>
            {DOMAIN_ICON[factor.domain]}
          </span>
          {factor.domain.replace(/-/g, ' ')}
        </span>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-subtle)',
        }}>
          {factor.jurisdiction.iso3}
          {factor.jurisdiction.subnational ? ` · ${factor.jurisdiction.subnational}` : ''}
          {' · '}{factor.pathogenClass}
        </span>
      </div>

      {/* CD R10 Q2: phenomenon prose carries always-visible citation
          superscripts [n]; each is hover/click for the source + verbatim quote. */}
      <div style={{
        fontSize: 13, color: 'var(--bm-text)', lineHeight: 1.5, marginBottom: 10,
      }}><PhenomenonWithCitations factor={factor} /></div>

      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
        fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
        marginBottom: 10,
      }}>
        <span
          title={ev.needs2Cites ? 'contested — requires ≥2 independent sources' : undefined}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: ev.cssVar,
          }}
        >
          <EvidenceDots strength={factor.evidenceStrength} />
          {ev.word} evidence
        </span>
        <span style={{
          color: 'var(--bm-text-subtle)', textTransform: 'uppercase' as const, letterSpacing: '.4px',
        }}>{factor.temporality}</span>
        <span
          title="Every citation round-trips via PMID/DOI/URL — hover the [n] superscripts above for source + verbatim quote"
          style={{ color: 'var(--bm-text-muted)' }}
        >▦ {factor.exampleCases.length} source{factor.exampleCases.length === 1 ? '' : 's'} · hover [n]</span>
      </div>

      {sensitive && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 7, alignItems: 'center',
          background: 'var(--bm-prov-synthetic-bg, rgba(140,131,184,0.15))',
          border: '1px solid var(--bm-prov-synthetic, var(--bm-blocked))',
          borderRadius: 6,
          padding: '7px 10px', marginBottom: 10,
        }}>
          <span style={{
            fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700,
            color: 'var(--bm-prov-synthetic, var(--bm-blocked))',
          }}>⚠ Handle with care</span>
          {factor.sensitivityFlags.map(s => (
            <span key={s} style={{
              fontFamily: 'var(--bm-font-mono)', fontSize: 10,
              color: 'var(--bm-prov-synthetic, var(--bm-blocked))',
              border: '1px solid var(--bm-prov-synthetic, var(--bm-blocked))',
              borderRadius: 4, padding: '1px 6px',
            }}>{SENSITIVITY_LABEL[s]}</span>
          ))}
        </div>
      )}

      <div style={{
        fontSize: 12, color: 'var(--bm-text-muted)', lineHeight: 1.5,
        background: 'var(--bm-bg-subtle)', borderRadius: 6, padding: '8px 10px',
      }}>
        <div style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 9.5,
          textTransform: 'uppercase' as const, letterSpacing: '.5px',
          color: 'var(--bm-accent, #6ea3ff)', marginBottom: 3,
        }}>Operator action</div>
        {factor.operatorAction.template
          ? `Apply template: ${factor.operatorAction.template} (surface context: ${factor.operatorAction.surfaceContext})`
          : `Surface to: ${factor.operatorAction.surfaceContext}`}
      </div>

      {factor.operatorAction.recommendedMessenger && factor.operatorAction.recommendedMessenger.length > 0 && (
        <div style={{
          fontSize: 11.5, color: 'var(--bm-text-muted)', marginTop: 8,
        }}>
          ◑ Recommended messenger: <b style={{ color: 'var(--bm-text)' }}>
            {factor.operatorAction.recommendedMessenger.join(' + ')}
          </b>
        </div>
      )}

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, marginTop: 10, paddingTop: 9, borderTop: '1px solid var(--bm-border)',
      }}>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 9.5, color: 'var(--bm-text-subtle)',
        }}>
          curated · {ev.needs2Cites ? '2-source minimum' : 'citation-traceable'} · {factor.lastReviewed}
        </span>
        {onSuppress && (
          <button
            type="button"
            onClick={onSuppress}
            style={{
              padding: '3px 10px', borderRadius: 5,
              fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 600,
              color: 'var(--bm-text-muted)',
              background: 'var(--bm-bg-subtle)',
              border: '1px solid var(--bm-border)',
              cursor: 'pointer',
            }}
          >{suppressed ? 'include' : 'suppress'}</button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Undercount overlay — CD R10 Q3: dotted "estimated-true" ghost line
// above the solid reported curve. Framed as a modelled data-limitation
// (V-Dem/RSF source on hover), NEVER as an accusation.
// ---------------------------------------------------------------------------

export function UndercountCurve({
  reported, modifier, modifierSource, width = 680, height = 230,
}: {
  reported: readonly number[];
  modifier: number;
  /** Brief source citation rendered in the legend + hover. */
  modifierSource: string;
  width?: number;
  height?: number;
}) {
  const padL = 50, padR = 16, padT = 16, padB = 30;
  const pw = width - padL - padR, ph = height - padT - padB;
  const estimated = reported.map(v => v * modifier);
  const max = Math.max(...estimated, 1);
  const n = reported.length;
  const X = (i: number) => padL + (i / Math.max(1, n - 1)) * pw;
  const Y = (v: number) => padT + ph - (v / max) * ph;
  const linePath = (a: readonly number[]) =>
    a.map((v, i) => `${i === 0 ? 'M' : 'L'} ${X(i)} ${Y(v)}`).join(' ');
  // Band area: estimated path forward + reported path reversed
  const bandPath =
    estimated.map((v, i) => `${i === 0 ? 'M' : 'L'} ${X(i)} ${Y(v)}`).join(' ')
    + ' ' + reported.map((_, i) => {
      const j = n - 1 - i;
      return `L ${X(j)} ${Y(reported[j]!)}`;
    }).join(' ')
    + ' Z';
  return (
    <div style={{
      background: 'var(--bm-bg-panel, var(--bm-bg))',
      border: '1px solid var(--bm-border)',
      borderRadius: 'var(--bm-radius-md)', padding: 16,
    }}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', display: 'block' }} role="img"
        aria-label={`Reported vs estimated-true curve (×${modifier} modifier from ${modifierSource})`}>
        {[0, 0.5, 1].map((g, k) => (
          <line key={k} x1={padL} x2={width - padR} y1={padT + ph * g} y2={padT + ph * g}
            stroke="var(--bm-border)" strokeWidth={0.5} />
        ))}
        <path d={bandPath} fill="var(--bm-prov-synthetic, var(--bm-blocked))" opacity={0.1} />
        <path d={linePath(estimated)} fill="none"
          stroke="var(--bm-prov-synthetic, var(--bm-blocked))" strokeWidth={2} strokeDasharray="5 4" />
        <path d={linePath(reported)} fill="none"
          stroke="var(--bm-prov-confirmed, var(--bm-state-live))" strokeWidth={2.4} />
        <text x={padL} y={height - 8}
          fontFamily="var(--bm-font-mono)" fontSize={10} fill="var(--bm-text-subtle)">reported vs estimated-true</text>
      </svg>
      <div style={{
        display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 4,
        fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-muted)',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            width: 14, height: 4, borderRadius: 2,
            background: 'var(--bm-prov-confirmed, var(--bm-state-live))',
          }} />
          Reported (official)
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          title={`Modifier source: ${modifierSource}`}>
          <span style={{
            width: 14, height: 0,
            borderTop: '2px dashed var(--bm-prov-synthetic, var(--bm-blocked))',
          }} />
          Estimated-true · ×{modifier}
        </span>
      </div>
      <div style={{
        marginTop: 10, padding: '10px 13px',
        fontSize: 12.5, color: 'var(--bm-text-muted)', lineHeight: 1.5,
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderLeft: '3px solid var(--bm-prov-synthetic, var(--bm-blocked))',
        borderRadius: 7,
      }}>
        <b style={{ color: 'var(--bm-prov-synthetic, var(--bm-blocked))' }}>
          ◇ Estimated-true is a modelled correction, not a counter-claim.
        </b>{' '}
        The ×{modifier} modifier is derived from independent press-freedom /
        governance indices ({modifierSource}) — shown on hover — and frames a{' '}
        <em>data limitation</em>, never an assertion that a government is lying.
        The reported curve stays primary and solid.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sidebar wrapper — collapsible right-rail sub-panel per CD R10 placement
// recommendation. Mounts on signal-detail surfaces (PerPathogenDrilldown,
// CountryProfile, MinisterBriefingPack auto-attach).
// ---------------------------------------------------------------------------

export function CulturalRiskSidebar({
  factors, title = 'Cultural / political context', iso3,
}: {
  factors: readonly CulturalRiskFactor[];
  title?: string;
  /** When set, the sidebar shows a "N pending curator review" pointer for
   *  this jurisdiction's politically-contested entries (CD R10 Q5), deep-
   *  linking to the Curate queue on §7.23 Operator Workspace. Omit on
   *  cross-jurisdiction (pathogen-scoped) mounts. */
  iso3?: string;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [suppressed, setSuppressed] = useState<Set<string>>(new Set());

  // CD R10 Q5 — pending-review pointer. Entries held from the live register
  // (politically-contested rail) awaiting human curator sign-off, scoped to
  // this jurisdiction. Deep-links to the Workspace Curate queue.
  const pendingForIso = useMemo(
    () => iso3 ? loadPendingCulturalRiskReview().filter(p => p.jurisdiction.iso3 === iso3) : [],
    [iso3],
  );

  const toggleSuppress = (id: string) => {
    setSuppressed(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (factors.length === 0) {
    return (
      <aside style={{
        padding: 14,
        background: 'var(--bm-bg-subtle)',
        border: '1px dashed var(--bm-border)',
        borderRadius: 'var(--bm-radius-md)',
        fontSize: 12, color: 'var(--bm-text-subtle)',
        lineHeight: 1.5,
      }}>
        <div style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700,
          textTransform: 'uppercase' as const, letterSpacing: '.5px',
          marginBottom: 6,
        }}>◑ {title}</div>
        No cultural / political risk factors curated for this (jurisdiction × pathogen-class) yet.
        Seed entries in Phase 9.2 via the register at
        {' '}<code>packages/plugin-region-database/data/cultural-risk-factors.json</code>.
        <PendingReviewPointer pending={pendingForIso} />
      </aside>
    );
  }

  return (
    <aside style={{
      background: 'var(--bm-bg-subtle)',
      border: '1px solid var(--bm-border)',
      borderRadius: 'var(--bm-radius-md)',
      padding: collapsed ? '8px 14px' : 14,
    }}>
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, marginBottom: collapsed ? 0 : 12,
      }}>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 11, fontWeight: 700,
          textTransform: 'uppercase' as const, letterSpacing: '.5px',
          color: 'var(--bm-text)',
        }}>◑ {title}{' '}
          <span style={{ color: 'var(--bm-text-subtle)' }}>· {factors.length} factor{factors.length === 1 ? '' : 's'}</span>
        </span>
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          style={{
            padding: '3px 9px',
            fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
            color: 'var(--bm-text-muted)',
            background: 'var(--bm-bg)',
            border: '1px solid var(--bm-border)',
            borderRadius: 5, cursor: 'pointer',
          }}
        >{collapsed ? '▾ expand' : '▴ collapse'}</button>
      </header>
      {!collapsed && (
        <div style={{ display: 'grid', gap: 12 }}>
          {factors.map(f => (
            <CulturalFactorCard
              key={f.id}
              factor={f}
              suppressed={suppressed.has(f.id)}
              onSuppress={() => toggleSuppress(f.id)}
            />
          ))}
        </div>
      )}
      {!collapsed && <PendingReviewPointer pending={pendingForIso} />}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// CD R10 Q5 — pending-review pointer. When a jurisdiction has entries held
// from the live register (politically-contested rail) awaiting human
// curator sign-off, surface a count + deep-link to the Curate queue on
// §7.23 Operator Workspace. The curator is an operator persona; sign-off is
// an audit event (see workflow/CurateQueue.tsx).
// ---------------------------------------------------------------------------

function PendingReviewPointer({ pending }: { pending: readonly CulturalRiskFactor[] }) {
  if (pending.length === 0) return null;
  return (
    <a
      href="#/operator-workspace?tab=curate"
      title="Review in Operator Workspace › Curate"
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        marginTop: 12, padding: '8px 11px',
        background: 'var(--bm-prov-synthetic-bg, rgba(140,131,184,0.15))',
        border: '1px solid var(--bm-prov-synthetic, var(--bm-blocked))',
        borderRadius: 7, textDecoration: 'none' as const,
      }}
    >
      <span style={{
        flexShrink: 0, width: 7, height: 7, borderRadius: '50%',
        background: 'var(--bm-prov-synthetic, var(--bm-blocked))',
      }} />
      <span style={{ flex: 1, fontSize: 11.5, lineHeight: 1.45, color: 'var(--bm-text-muted)' }}>
        <b style={{ color: 'var(--bm-text)' }}>{pending.length} entr{pending.length === 1 ? 'y' : 'ies'} pending curator review</b>
        {' '}for this jurisdiction
        <span style={{
          display: 'block',
          fontFamily: 'var(--bm-font-mono)', fontSize: 10,
          color: 'var(--bm-text-subtle)',
        }}>politically-contested rail · review in Workspace › Curate</span>
      </span>
      <span style={{
        flexShrink: 0,
        fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700,
        color: 'var(--bm-prov-synthetic, var(--bm-blocked))',
      }}>Open →</span>
    </a>
  );
}
